import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { Plus, X } from 'lucide-react';
import * as React from 'react';

import {
  GROUP_WORDS,
  newGroup,
  newLeaf,
  type IPredicateGroup,
  type IPredicateProblem,
  type PredicateNode
} from '../../concern-predicate';
import { ConditionRow } from './condition-row';

/**
 * A rule's conditions — the form's third step (SR-227, R8 and R10, design
 * `docs/superpowers/specs/2026-10-05-concern-criteria-builder-ui.md` §3.3 and §5).
 *
 * **A group is recursive, to any depth** (Q2, ruled (a)): its header chooses "all" or "any", its
 * items are conditions or groups, and each group has its own "Add a condition" and "Add a group"
 * after its items. survey-api's grammar nests without bound, and the reading view must say any
 * depth anyway; the rejected one-level cap would be a judgment the parser does not make.
 *
 * **A group is never empty**: the parser refuses an empty array, so a group emptied by removals
 * keeps one blank condition — whose own refusal ("is required" at `.member`) then lands beside it
 * like any other — rather than vanishing under the person's cursor.
 *
 * **Each refusal lands on the node whose path it names** (`placeProblems`): a condition's beside
 * its row, a group's under its header, and a path no node carries listed under the whole block
 * in survey-api's words — never dropped, never guessed onto a row.
 */
export function ConditionBlock({
  root,
  paths,
  problemsOf,
  unplaced,
  suggestions,
  onChange
}: {
  root: IPredicateGroup;
  /** Node id → its JSON path — `writePredicate`'s. */
  paths: ReadonlyMap<string, string>;
  problemsOf: (nodeId: string) => readonly string[];
  unplaced: readonly IPredicateProblem[];
  suggestions: ReadonlyMap<string, readonly string[]>;
  onChange: (root: IPredicateGroup) => void;
}) {
  // Conditions are numbered across the whole rule, in reading order, so no two rows share a name.
  const numbers = React.useMemo(() => {
    const held = new Map<string, number>();
    const visit = (node: PredicateNode) => {
      if (node.type === 'group') node.items.forEach(visit);
      else held.set(node.id, held.size + 1);
    };
    visit(root);
    return held;
  }, [root]);

  return (
    <div className="grid gap-2">
      <ConditionGroup
        group={root}
        paths={paths}
        numbers={numbers}
        problemsOf={problemsOf}
        suggestions={suggestions}
        onChange={onChange}
      />
      {unplaced.length > 0 && (
        <div role="alert" className="grid gap-1 text-destructive">
          <p>The system refused these parts of the conditions:</p>
          <ProblemList problems={unplaced} />
        </div>
      )}
    </div>
  );
}

function ConditionGroup({
  group,
  paths,
  numbers,
  problemsOf,
  suggestions,
  onChange,
  onRemove
}: {
  group: IPredicateGroup;
  paths: ReadonlyMap<string, string>;
  numbers: ReadonlyMap<string, number>;
  problemsOf: (nodeId: string) => readonly string[];
  suggestions: ReadonlyMap<string, readonly string[]>;
  onChange: (group: IPredicateGroup) => void;
  /** A nested group's "Remove group"; the root has none. */
  onRemove?: () => void;
}) {
  const nested = onRemove !== undefined;
  const setItems = (items: PredicateNode[]) =>
    onChange({ ...group, items: items.length > 0 ? items : [newLeaf()] });
  const setItem = (index: number, item: PredicateNode) =>
    setItems(group.items.map((held, at) => (at === index ? item : held)));
  const removeItem = (index: number) => setItems(group.items.filter((_, at) => at !== index));

  const match = (
    <Select value={group.op} onValueChange={(op) => onChange({ ...group, op: op as 'all' | 'any' })}>
      <SelectTrigger aria-label={nested ? 'Match in this group' : 'Match'} className="w-20">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">all</SelectItem>
        <SelectItem value="any">any</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <div
      data-group={paths.get(group.id)}
      className={'grid gap-2' + (nested ? ' ml-1 border-l-2 border-border pl-4' : '')}
    >
      <div className="flex flex-wrap items-center gap-2">
        {!nested && 'Match a disclosure where'}
        {match}
        {GROUP_WORDS[group.op]}
        {nested && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-muted-foreground"
            onClick={onRemove}
          >
            <X className="size-3.5" aria-hidden />
            Remove group
          </Button>
        )}
      </div>
      {problemsOf(group.id).map((reason, index) => (
        <p key={index} role="alert" className="text-destructive">
          {reason}
        </p>
      ))}
      {group.items.map((item, index) =>
        item.type === 'group' ? (
          <ConditionGroup
            key={item.id}
            group={item}
            paths={paths}
            numbers={numbers}
            problemsOf={problemsOf}
            suggestions={suggestions}
            onChange={(next) => setItem(index, next)}
            onRemove={() => removeItem(index)}
          />
        ) : (
          <ConditionRow
            key={item.id}
            leaf={item}
            number={numbers.get(item.id) ?? index + 1}
            path={paths.get(item.id) ?? ''}
            problems={problemsOf(item.id)}
            suggestions={suggestions}
            onChange={(next) => setItem(index, next)}
            onRemove={() => removeItem(index)}
          />
        )
      )}
      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => setItems([...group.items, newLeaf()])}
        >
          <Plus className="size-3.5" aria-hidden />
          Add a condition
        </Button>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          // A group inside "all" is usually an "any", and the reverse — the other word first.
          onClick={() => setItems([...group.items, newGroup(group.op === 'all' ? 'any' : 'all')])}
        >
          <Plus className="size-3.5" aria-hidden />
          Add a group
        </Button>
      </div>
    </div>
  );
}

/** Problems survey-api answered at paths no control carries, listed as it named them. */
export function ProblemList({ problems }: { problems: readonly IPredicateProblem[] }) {
  return (
    <ul className="grid gap-1">
      {problems.map((problem, index) => (
        <li key={index}>
          <code className="font-mono">{problem.target}</code>: {problem.reason}
        </li>
      ))}
    </ul>
  );
}
